import React, { useId } from 'react'
import {
    BreadCrumbs,
    CircleLoadingArrowIcon,
    HomeIcon,
    PaginatorRightArrowIcon,
    PaginatorWrapper,
    TextBody,
    TextHeading,
    TransparentButtonWrapper,
} from '@isdd/idsk-ui-kit/index'
import { FlexColumnReverseWrapper } from '@isdd/metais-common/components/flex-column-reverse-wrapper/FlexColumnReverseWrapper'
import { NavigationSubRoutes } from '@isdd/metais-common/navigation/routeNames'
import { IFilter, Pagination } from '@isdd/idsk-ui-kit/types'
import { HierarchyRightsUi } from '@isdd/metais-common/api/generated/cmdb-swagger'
import { QueryFeedback } from '@isdd/metais-common/index'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import styles from './styles.module.scss'
import { PublicAuthoritySelect } from './PublicAuthoritySelect'

import { MainContentWrapper } from '@/components/MainContentWrapper'
import { PublicAuthoritiesHierarchyItem } from '@/components/containers/public-authorities-hierarchy/PublicAuthoritiesHierarchyContainer'
interface IPublicAuthoritiesHierarchyView {
    hierarchy: PublicAuthoritiesHierarchyItem[]
    pagination: Pagination
    handleFilterChange: (filter: IFilter) => void
    onChangeAuthority: (e: HierarchyRightsUi | null) => void
    selectedOrg: HierarchyRightsUi | null
    handleExpandClick: (row: PublicAuthoritiesHierarchyItem) => void
    isLoading: boolean
    expandableRowIdLoading: string | null
    expanded: Record<string, boolean>
}

export const PublicAuthoritiesHierarchyView: React.FC<IPublicAuthoritiesHierarchyView> = ({
    hierarchy,
    pagination,
    handleFilterChange,
    onChangeAuthority,
    selectedOrg,
    handleExpandClick,
    isLoading,
    expandableRowIdLoading,
    expanded,
}) => {
    const { t } = useTranslation()

    const ExpandableList = ({ wrapperId, items }: { wrapperId: string; items?: PublicAuthoritiesHierarchyItem[] }) => {
        const id = useId()
        return (
            <ul className={styles.expandableList} id={wrapperId}>
                {items?.map((item, key) => {
                    const itemIsExpanded = expanded[item.uuid]

                    return (
                        <li key={key} className={styles.rowWrapper}>
                            <div className={styles.itemWrapper}>
                                <div className={styles.toggleWrapper}>
                                    {(item.canExpand || (item.children?.length ?? 0) > 0) &&
                                        (expandableRowIdLoading === item.uuid ? (
                                            <img className={styles.spinner} src={CircleLoadingArrowIcon} alt={t('loading.subitems')} />
                                        ) : (
                                            <TransparentButtonWrapper
                                                onClick={() => handleExpandClick(item)}
                                                aria-label={
                                                    itemIsExpanded
                                                        ? t('publicAuthorities.collapse', { rowName: item.name })
                                                        : t('publicAuthorities.expand', { rowName: item.name })
                                                }
                                                aria-expanded={itemIsExpanded ? 'true' : 'false'}
                                                aria-controls={id}
                                            >
                                                <img
                                                    src={PaginatorRightArrowIcon}
                                                    className={classNames([styles.expandIcon, itemIsExpanded ? styles.rotate90 : styles.rotate0])}
                                                    alt=""
                                                />
                                            </TransparentButtonWrapper>
                                        ))}
                                </div>

                                <TextBody size="S" className={styles.rowDataWrapper}>
                                    {item.name}
                                    <small className={styles.address}>{item.address}</small>
                                </TextBody>
                            </div>
                            {(item.canExpand || (item.children?.length ?? 0) > 0) && (
                                <div id={id}>{itemIsExpanded && item.children && <ExpandableList items={item.children ?? []} wrapperId={id} />}</div>
                            )}
                        </li>
                    )
                })}
            </ul>
        )
    }

    return (
        <>
            <BreadCrumbs
                withWidthContainer
                links={[
                    { label: t('notifications.home'), href: '/', icon: HomeIcon },
                    { label: t('publicAuthorities.hierarchy'), href: NavigationSubRoutes.PUBLIC_AUTHORITY_HIERARCHY },
                ]}
            />
            <MainContentWrapper>
                <FlexColumnReverseWrapper>
                    <TextHeading size="XL">{t('publicAuthorities.hierarchy')}</TextHeading>
                </FlexColumnReverseWrapper>
                <PublicAuthoritySelect onChangeAuthority={onChangeAuthority} selectedOrg={selectedOrg} isClearable />

                <QueryFeedback loading={isLoading && !expandableRowIdLoading} withChildren>
                    <ExpandableList wrapperId="publicAuthorities_hierarchy" items={hierarchy} />
                    <PaginatorWrapper {...pagination} handlePageChange={handleFilterChange} />
                </QueryFeedback>
            </MainContentWrapper>
        </>
    )
}
